<script>
import Pagination from "../mixins/Pagination";
import ApiService from "../utils/ApiService";
import ModalSendMail from "../components/ModalSendMail.vue";
import FormDetailPanel from "./FormDetailPanel.vue";
import BulkBar from "../components/BulkBar.vue";
import {isAdministrator} from "../layout/navigation";
import {FORM_TYPE_LABELS, formMeta, formStatus, formatMoney, formatDmy, tenantsOf, isRentIncrease, rtb7Effects, effectsHtml, leaseEndsBefore} from "./formHelpers";

// Stands for the signed-in user in a chip's filters.
const ME = '__me__';

// Quick filters per form type. Each maps to list filters understood by SterlingFormFilter.
const CHIPS = {
    rtb7: [
        {key: 'pending', label: 'Needs confirmation', params: {confirmation: 'pending'}},
        {key: 'to_serve', label: 'Confirmed · not served', params: {confirmation: 'confirmed', served: '0'}},
        {key: 'sent', label: 'Sent', params: {sent_status: '1'}},
        {key: 'draft', label: 'Drafts', params: {status: 'draft'}},
        {key: 'overdue', label: 'Overdue', params: {overdue: '1'}},
    ],
    get rtb11a() {
        return this.rtb7;
    },
    'inspection-report': [
        {key: 'review', label: 'Waiting for PM', params: {status: 'review'}},
        {key: 'tosign', label: 'To sign by me', params: {status: 'review', reviewer: ME}},
        {key: 'draft', label: 'Drafts', params: {status: 'draft'}},
        {key: 'sent', label: 'Sent', params: {sent_status: '1'}},
    ],
    other: [
        {key: 'draft', label: 'Drafts', params: {status: 'draft'}},
        {key: 'sent', label: 'Sent', params: {sent_status: '1'}},
    ],
};
const CHIP_KEYS = ['confirmation', 'served', 'sent_status', 'status', 'reviewer', 'overdue'];

export default {
    name: "FormListView",
    components: {ModalSendMail, FormDetailPanel, BulkBar},
    mixins: [Pagination],
    props: {
        formType: {type: String, default: ''},
        // The /forms page switches type with tabs; the inspection page is fixed.
        showTabs: {type: Boolean, default: false},
    },
    data() {
        return {
            params: {
                q: '', type: '', date: '', user_id: '', sort: 'id-desc',
                status: '', confirmation: '', served: '', sent_status: '', unit_id: '', reviewer: '', overdue: '',
                manager_id: '',
            },
            chip: '',
            mine: false,
            moreFilters: false,
            pageSize: 50,
            agents: [],
            managers: [],
            canSend: true,
            statusList: [
                {label: 'Draft', value: 'draft'},
                {label: 'Sent', value: 'sent'},
                {label: 'Published', value: 'published'},
                {label: 'Waiting for PM', value: 'review'},
                {label: 'Archived', value: 'archived'},
            ],
            tabs: [
                {label: 'Rent increase (RTB-7)', value: 'rtb7'},
                {label: 'Park sites (RTB-11a)', value: 'rtb11a'},
                {label: 'End of tenancy (RTB-30)', value: 'rtb30'},
                {label: 'Inspection reports', value: 'inspection-report'},
                {label: 'All forms', value: ''},
            ],
            detail: null,
            targetIds: [],
            forwardDialog: false,
            forwardTo: '',
            searchTimer: null,
            sendWarned: false,
            sendNotes: [],
            onSend() {
            },
        }
    },
    computed: {
        userId() {
            return (this.$store.getters.userInfo || {}).id;
        },
        chips() {
            return CHIPS[this.params.type] || CHIPS.other;
        },
        isRtb7() {
            return this.params.type === 'rtb7' || this.params.type === 'rtb11a';
        },
        selected() {
            return this.selectionIds || [];
        },
        isAdmin() {
            return isAdministrator(this.$store.getters.userInfo || {});
        },
        // Drafts the office is checking before their managers get them.
        selectedHeld() {
            return this.selected.filter(f => f.status === 'draft' && formMeta(f, 'held_for'));
        },
        canConfirmSelection() {
            return this.selected.some(this.needsConfirmation);
        },
    },
    watch: {
        // Menu links to /forms?type=… reuse this component; follow the query.
        '$route.query'(q, old) {
            if ((q.type || '') !== (old.type || '') || ['confirmation', 'mine', 'served', 'status', 'user_id', 'unit_id', 'manager_id'].some(k => q[k] !== old[k])) {
                this.applyQuery();
                this.onSearch();
            }
            if (q.open && q.open !== old.open) {
                this.openById(q.open);
            }
        },
        'params.q'() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(this.onSearch, 350);
        },
    },
    methods: {
        listApi() {
            return '/sterling/forms';
        },
        listParams() {
            return {...this.params, user_id: this.mine ? this.userId : this.params.user_id};
        },
        typeLabel: (t) => FORM_TYPE_LABELS[t] || t,
        meta: formMeta,
        status: formStatus,
        money: formatMoney,
        dmy: formatDmy,
        tenants: tenantsOf,
        leaseEnds: leaseEndsBefore,
        needsConfirmation(form) {
            return isRentIncrease(form) && !!formMeta(form, 'auto_generated') && !formMeta(form, 'pm_confirmed_at')
                && form.status === 'draft';
        },
        applyQuery() {
            const q = (this.$route && this.$route.query) || {};
            this.params.type = this.showTabs ? (q.type || '') : this.formType;
            CHIP_KEYS.forEach(k => {
                this.params[k] = '';
            });
            this.chip = '';
            const match = this.chips.find(c => Object.keys(c.params).every(k => String(q[k] || '') === c.params[k]));
            if (match) {
                this.chip = match.key;
                Object.assign(this.params, match.params);
            } else {
                ['confirmation', 'served', 'status'].forEach(k => {
                    if (q[k] !== undefined) this.params[k] = String(q[k]);
                });
            }
            this.mine = !!q.mine;
            this.params.user_id = q.user_id && !this.mine ? Number(q.user_id) || '' : '';
            this.params.unit_id = q.unit_id ? Number(q.unit_id) || '' : '';
            this.params.manager_id = q.manager_id === 'none' ? 'none' : (Number(q.manager_id) || '');
        },
        // The property's rental manager in Buildium (the list loads unit.property with each form).
        managerName(row) {
            const m = row.unit && row.unit.property && row.unit.property.RentalManager;
            return m && m.Id ? [m.FirstName, m.LastName].filter(Boolean).join(' ') : '';
        },
        clearUnit() {
            const query = {...this.$route.query};
            delete query.unit_id;
            this.$router.replace({path: this.$route.path, query}).catch(() => {
            });
        },
        setTab(type) {
            if ((this.params.type || '') === type) return;
            this.$router.replace({path: this.$route.path, query: type ? {type} : {}}).catch(() => {
            });
        },
        toggleChip(chip) {
            CHIP_KEYS.forEach(k => {
                this.params[k] = '';
            });
            this.chip = this.chip === chip.key ? '' : chip.key;
            if (this.chip) {
                Object.keys(chip.params).forEach(k => {
                    this.params[k] = chip.params[k] === ME ? this.userId : chip.params[k];
                });
            }
            this.onSearch();
        },
        toggleMine() {
            this.mine = !this.mine;
            this.onSearch();
        },
        clearFilters() {
            this.params.q = '';
            this.params.date = '';
            this.params.user_id = '';
            this.params.manager_id = '';
            CHIP_KEYS.forEach(k => {
                this.params[k] = '';
            });
            this.chip = '';
            this.mine = false;
            this.onSearch();
        },
        onRowClick(row, column) {
            if (column && column.type === 'selection') return;
            this.detail = row;
        },
        rowClass({row}) {
            return this.detail && this.detail.id === row.id ? 'is-open' : '';
        },
        openById(id) {
            const found = this.dataList.find(f => String(f.id) === String(id));
            if (found) {
                this.detail = found;
                return;
            }
            ApiService.get(`/sterling/forms/${id}`).then(r => {
                this.detail = r.data;
            }).catch(() => {
                this.$message.error('That form no longer exists');
            });
        },
        onFinish() {
            // Keep the open panel in step with the refreshed row.
            if (this.detail) {
                const fresh = this.dataList.find(f => f.id === this.detail.id);
                if (fresh) this.detail = fresh;
            }
        },
        ids(list) {
            return (list || this.selected).map(d => d.id);
        },

        // ---- actions (on the selection, or on one form from the panel) ----
        // Confirm, email the tenants with the tenant template and enter the new rent in Buildium.
        confirmSend(forms) {
            const list = (forms || this.selected).filter(f => isRentIncrease(f) && f.status === 'draft');
            const ids = this.ids(list);
            if (!ids.length) {
                this.$message.warning('Only draft rent increase notices can be confirmed and sent.');
                return;
            }
            const fx = rtb7Effects('confirm-send', list);
            this.$confirm(effectsHtml(fx), fx.title, {confirmButtonText: fx.button, dangerouslyUseHTMLString: true}).then(() => {
                this.loading = true;
                ApiService.post('/sterling/forms/confirm-send', {ids}).then(r => {
                    const {sent, in_person, refused} = r.data;
                    let msg = `${sent.length} sent to tenants`;
                    if (in_person.length) msg += `; ${in_person.length} confirmed for serving in person (no tenant email)`;
                    if (refused.length) msg += `; ${refused.length} not sent (` + [...new Set(refused.map(x => x.reason))].join(', ') + ')';
                    this.$message({type: refused.length ? 'warning' : 'success', message: msg, duration: 8000});
                    this.$refs.table && this.$refs.table.clearSelection();
                    this.fetchList();
                }).catch(reason => {
                    this.$message.error(reason.message);
                }).finally(() => {
                    this.loading = false;
                });
            }).catch(() => {
            });
        },
        confirm(forms) {
            const ids = this.ids(forms);
            const fx = rtb7Effects('confirm', forms || this.selected);
            this.$confirm(effectsHtml(fx), fx.title, {confirmButtonText: fx.button, dangerouslyUseHTMLString: true})
                .then(() => this.doConfirm(ids)).catch(() => {
                });
        },
        doConfirm(ids) {
            this.loading = true;
            ApiService.post('/sterling/forms/confirm', {ids}).then(response => {
                const {confirmed, skipped} = response.data;
                let msg = `${confirmed.length} notice(s) confirmed`;
                if (skipped.length) {
                    msg += `; ${skipped.length} skipped (` + [...new Set(skipped.map(s => s.reason))].join(', ') + ')';
                }
                this.$message({type: skipped.length ? 'warning' : 'success', message: msg});
                this.fetchList();
            }).catch(reason => {
                this.$message.error(reason.message);
            }).finally(() => {
                this.loading = false;
            });
        },
        forward(forms) {
            this.targetIds = this.ids(forms);
            this.forwardTo = '';
            this.forwardDialog = true;
        },
        releaseSelected() {
            const ids = this.selectedHeld.map(f => f.id);
            this.$confirm(`Hand these ${ids.length} checked draft(s) to their property managers? Each manager gets one email with a personal link to confirm and send them; the administrator gets a copy without the link. Nothing reaches tenants and Buildium is not changed until a manager confirms & sends.`,
                'Release', {confirmButtonText: `Release ${ids.length}`}).then(() => {
                this.loading = true;
                ApiService.post('/sterling/rent-increase/config', {action: 'release_held', ids}).then(r => {
                    const {released, managers, still_held} = r.data;
                    this.$message.success(`${released} released to ${managers} property manager(s)` + (still_held ? `; ${still_held} still waiting for the check` : ''));
                    this.$refs.table && this.$refs.table.clearSelection();
                    this.fetchList();
                }).catch(reason => {
                    this.$message.error(reason.message);
                }).finally(() => {
                    this.loading = false;
                });
            }).catch(() => {
            });
        },
        submitForward() {
            this.loading = true;
            ApiService.post('/sterling/forms/assign', {ids: this.targetIds, user_id: this.forwardTo}).then(response => {
                const {forwarded, skipped} = response.data;
                this.$message({
                    type: skipped.length ? 'warning' : 'success',
                    message: `${forwarded.length} notice(s) forwarded` + (skipped.length ? `; ${skipped.length} skipped (not a draft, or not yours to forward)` : '')
                });
                this.forwardDialog = false;
                this.fetchList();
            }).catch(reason => {
                this.$message.error(reason.message);
            }).finally(() => {
                this.loading = false;
            });
        },
        send(forms) {
            const list = forms || this.selected;
            const sent = list.filter(f => f.sent_at);
            if (sent.length && !this.sendWarned) {
                this.$confirm(`${sent.length} of these ${list.length} were already emailed. The recipients will get them again.`,
                    'Already sent', {confirmButtonText: 'Continue', cancelButtonText: 'Cancel', type: 'warning'}).then(() => {
                    this.sendWarned = true;
                    this.send(list);
                }).catch(() => {
                });
                return;
            }
            this.sendWarned = false;
            const ids = this.ids(list);
            this.sendNotes = list.length && list.every(f => isRentIncrease(f)) ? rtb7Effects('send', list).lines : [];
            this.onSend = (options) => {
                this.loading = true;
                ApiService.post('/sterling/forms/send', {ids, options}).then(() => {
                    this.$message.success(`${ids.length} form(s) queued for sending`);
                    this.fetchList();
                }).catch(reason => {
                    this.$message.error(reason.message);
                }).finally(() => {
                    this.loading = false;
                });
            };
            this.$refs.mailer.open();
        },
        download(forms) {
            const ids = this.ids(forms);
            this.loading = true;
            ApiService.post('/sterling/forms/download', {ids}).then(response => {
                window.open(response.data.url);
            }).catch(reason => {
                this.$message.error(reason.message);
            }).finally(() => {
                this.loading = false;
            });
        },
        // Publishing uploads the PDF to the lease's files in Buildium. A form that is already
        // there gets a second copy, so those are left out unless asked for.
        publish(forms) {
            const list = forms || this.selected;
            const done = list.filter(f => f.published_at || ['published', 'publishing'].includes(f.status));
            const fresh = list.filter(f => !done.includes(f));
            const run = (ids) => {
                this.loading = true;
                ApiService.post('/sterling/forms/publish', {ids}).then(() => {
                    this.$message.success(`${ids.length} form(s) queued for publishing`);
                    this.fetchList();
                }).catch(reason => {
                    this.$message.error(reason.message);
                }).finally(() => {
                    this.loading = false;
                });
            };
            if (!done.length) {
                const rtb7 = fresh.length && fresh.every(f => isRentIncrease(f));
                const fx = rtb7Effects('publish', fresh);
                this.$confirm(rtb7 ? effectsHtml(fx) : `Upload ${fresh.length} form(s) to Buildium?`, 'Publish',
                    {confirmButtonText: `Publish ${fresh.length}`, dangerouslyUseHTMLString: rtb7})
                    .then(() => run(this.ids(fresh))).catch(() => {
                    });
                return;
            }
            const msg = `${done.length} of these ${list.length} are already in Buildium. Publishing them again adds another copy of the file there.`;
            this.$confirm(msg, 'Already published', {
                confirmButtonText: fresh.length ? `Publish the ${fresh.length} new only` : 'Publish again anyway',
                cancelButtonText: fresh.length ? `Publish all ${list.length} again` : 'Cancel',
                distinguishCancelAndClose: true,
                type: 'warning',
            }).then(() => run(this.ids(fresh.length ? fresh : list))).catch(action => {
                if (action === 'cancel' && fresh.length) run(this.ids(list));
            });
        },
        exportForms(forms) {
            const ids = this.ids(forms);
            this.loading = true;
            ApiService.post('/sterling/forms/export', {ids}).then(response => {
                window.open(response.data.url);
            }).catch(reason => {
                this.$message.error(reason.message);
            }).finally(() => {
                this.loading = false;
            });
        },
        remove(forms) {
            const ids = this.ids(forms);
            const what = ids.length === 1 ? 'this form' : `these ${ids.length} forms`;
            this.$confirm(`Delete ${what} permanently? The PDF and its history are removed and this cannot be undone.`,
                'Delete forms', {type: 'warning', confirmButtonText: `Delete ${ids.length}`, confirmButtonClass: 'el-button--danger'}).then(() => {
                this.loading = true;
                ApiService.delete('/sterling/forms/batch', {data: {ids}}).then(() => {
                    if (this.detail && ids.includes(this.detail.id)) this.detail = null;
                    this.$refs.table && this.$refs.table.clearSelection();
                    this.$message.success(`${ids.length} form(s) deleted`);
                    this.fetchList();
                }).catch(reason => {
                    this.$message.error(reason.message);
                }).finally(() => {
                    this.loading = false;
                });
            }).catch(() => {
            });
        },
        // "No increase this year": the drafts are archived and the monthly run leaves these
        // leases alone until the same date next year.
        skipIncrease(forms) {
            const ids = this.ids(forms);
            const fx = rtb7Effects('skip', forms || this.selected);
            this.$confirm(effectsHtml(fx), fx.title, {confirmButtonText: fx.button, dangerouslyUseHTMLString: true}).then(() => {
                this.loading = true;
                ApiService.post('/sterling/forms/skip-increase', {ids}).then(r => {
                    const {skipped, refused} = r.data;
                    let msg = `${skipped.length} lease(s) left at their rent this year`;
                    if (refused.length) {
                        msg += `; ${refused.length} not changed (` + [...new Set(refused.map(x => x.reason))].join(', ') + ')';
                    }
                    this.$message({type: refused.length ? 'warning' : 'success', message: msg});
                    this.$refs.table && this.$refs.table.clearSelection();
                    this.fetchList();
                }).catch(reason => {
                    this.$message.error(reason.message);
                }).finally(() => {
                    this.loading = false;
                });
            }).catch(() => {
            });
        },
        withdrawIncrease(forms) {
            const list = (forms || this.selected).filter(f => isRentIncrease(f) && f.status !== 'draft');
            if (!list.length) {
                this.$message.warning('Only sent or published rent increase notices can be marked as withdrawn.');
                return;
            }
            const fx = rtb7Effects('withdraw', list);
            this.$confirm(effectsHtml(fx), fx.title, {confirmButtonText: fx.button, dangerouslyUseHTMLString: true}).then(() => {
                this.loading = true;
                ApiService.post('/sterling/forms/withdraw-increase', {ids: this.ids(list)}).then(r => {
                    this.$message.success(`${r.data.withdrawn.length} notice(s) marked as withdrawn`);
                    this.$refs.table && this.$refs.table.clearSelection();
                    this.fetchList();
                }).catch(reason => {
                    this.$message.error(reason.message);
                }).finally(() => {
                    this.loading = false;
                });
            }).catch(() => {
            });
        },
        onBulkMore(cmd) {
            if (cmd === 'skip') this.skipIncrease();
            if (cmd === 'withdraw') this.withdrawIncrease();
            if (cmd === 'publish') this.publish();
            if (cmd === 'export') this.exportForms();
            if (cmd === 'delete') this.remove();
        },
        onListMore(cmd) {
            if (cmd === 'sendall') this.sendAll();
            if (cmd === 'downloadall') this.downloadAll();
        },
        sendAll() {
            this.onSend = (options) => {
                this.loading = true;
                ApiService.post('/sterling/forms/send', {action: 'sendall', options}, {params: this.listParams()}).then(response => {
                    const skipped = response.data?.skipped_unconfirmed || 0;
                    this.$message[skipped ? 'warning' : 'success'](skipped
                        ? `Queued; ${skipped} notice(s) left out: not yet confirmed by their property manager`
                        : 'Queued for sending');
                }).catch(reason => {
                    this.$message.error(reason.message);
                }).finally(() => {
                    this.loading = false;
                });
            };
            this.$refs.mailer.open();
        },
        downloadAll() {
            this.$confirm(`Download the PDFs of all ${this.total} matching form(s) as one file?`, 'Download all').then(() => {
                this.loading = true;
                ApiService.post('/sterling/forms/download', {action: 'downloadall', params: this.listParams()}).then(response => {
                    window.open(response.data.url);
                }).catch(reason => {
                    this.$message.error(reason.message);
                }).finally(() => {
                    this.loading = false;
                });
            }).catch(() => {
            });
        },
        fetchMode() {
            ApiService.get('/sterling/forms/rtb7-mode').then(r => {
                this.canSend = !!(r.data && r.data.can_send);
            }).catch(() => {
            });
        },
        fetchManagers() {
            ApiService.get('/sterling/forms/managers').then(response => {
                this.managers = response.data || [];
            }).catch(() => {
                this.managers = [];
            });
        },
        fetchAgents() {
            ApiService.get('/users', {params: {limit: 100}}).then(response => {
                this.agents = response.data.items;
            }).catch(() => {
                this.agents = [];
            });
        },
    },
    mounted() {
        const q = (this.$route && this.$route.query) || {};
        // Older emails link the settings here; the run page replaced the dialog.
        if (q.settings) {
            this.$router.replace('/rent-increase').catch(() => {
            });
            return;
        }
        this.applyQuery();
        this.fetchList();
        this.fetchAgents();
        this.fetchManagers();
        this.fetchMode();
        if (q.open) this.openById(q.open);
    },
}
</script>

<template>
    <div class="forms-view" :class="{'has-panel': !!detail}">
        <div v-if="showTabs" class="list-tabs" role="tablist" aria-label="Form type">
            <button v-for="t in tabs" :key="t.value" type="button" role="tab"
                    :aria-selected="String((params.type || '') === t.value)"
                    :class="{on: (params.type || '') === t.value}" @click="setTab(t.value)">{{ t.label }}
            </button>
        </div>

        <div class="list-filters">
            <el-input v-model="params.q" class="list-search" clearable prefix-icon="el-icon-search"
                      placeholder="Address or unit" aria-label="Filter by address or unit"/>
            <button v-for="c in chips" :key="c.key" type="button" class="chip" :class="{on: chip === c.key}"
                    :aria-pressed="String(chip === c.key)" @click="toggleChip(c)">{{ c.label }}
            </button>
            <button v-if="params.unit_id" type="button" class="chip on" @click="clearUnit" aria-label="Remove the unit filter">
                One unit <i class="el-icon-close"></i>
            </button>
            <button type="button" class="chip" :class="{on: mine}" :aria-pressed="String(mine)" @click="toggleMine">
                Assigned to me
            </button>
            <el-select v-model="params.manager_id" placeholder="PM (Buildium)" clearable filterable size="small"
                       style="width: 170px" aria-label="Filter by the property's rental manager in Buildium" @change="onSearch">
                <el-option v-for="m in managers" :key="m.id" :label="m.name" :value="m.id"/>
                <el-option label="No PM in Buildium" value="none"/>
            </el-select>
            <button type="button" class="chip chip-more" :aria-expanded="String(moreFilters)" @click="moreFilters = !moreFilters">
                {{ moreFilters ? 'Fewer filters' : '+ Date, agent, status' }}
            </button>
            <span class="spacer"></span>
            <el-dropdown trigger="click" @command="onListMore">
                <el-button size="small">All {{ total }} matching <i class="el-icon-arrow-down el-icon--right"></i></el-button>
                <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item command="sendall">Send all by email…</el-dropdown-item>
                    <el-dropdown-item command="downloadall">Download all PDFs</el-dropdown-item>
                </el-dropdown-menu>
            </el-dropdown>
        </div>
        <div v-if="moreFilters" class="list-filters list-filters-more">
            <el-date-picker v-model="params.date" format="yyyy-MM-dd" value-format="yyyy-MM-dd" placeholder="Created on"
                            size="small" @change="onSearch"/>
            <el-select v-model="params.user_id" placeholder="Assigned to" clearable filterable size="small" :disabled="mine"
                       @change="onSearch">
                <el-option v-for="item in agents" :key="item.id" :label="item.nickname" :value="item.id"/>
            </el-select>
            <el-select v-model="params.status" placeholder="Status" clearable size="small" @change="onSearch">
                <el-option v-for="item in statusList" :key="item.value" :label="item.label" :value="item.value"/>
            </el-select>
            <el-button size="small" type="text" @click="clearFilters">Clear all filters</el-button>
        </div>

        <section class="list-card" v-loading="loading">
            <el-table ref="table" :data="dataList" @selection-change="onSelectionChange" @row-click="onRowClick"
                      :row-class-name="rowClass" class="list-table">
                <el-table-column width="44" type="selection"/>
                <el-table-column label="Unit" min-width="260">
                    <template slot-scope="{row}">
                        <div class="cell-title">{{ row.unit_address || ('Form #' + row.id) }}</div>
                        <div class="cell-sub">
                            <span v-if="row.unit && row.unit.property && row.unit.property.custom_id">{{ row.unit.property.custom_id }} · </span>
                            <span v-if="!params.type">{{ typeLabel(row.form_type) }} · </span>
                            <span v-if="tenants(row).length">{{ tenants(row).length }} tenant{{ tenants(row).length > 1 ? 's' : '' }}</span>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column v-if="isRtb7" label="Rent change" width="175">
                    <template slot-scope="{row}">
                        <span v-if="meta(row, 'rent_new')">{{ money(meta(row, 'rent_current')) }} → <b>{{ money(meta(row, 'rent_new')) }}</b></span>
                    </template>
                </el-table-column>
                <el-table-column v-if="isRtb7" label="Effective" width="120">
                    <template slot-scope="{row}">
                        {{ dmy(meta(row, 'rent_new_payable_date')) }}
                        <div v-if="Number(meta(row, 'overdue_months')) > 0" class="cell-sub"
                             :title="'The last increase was ' + (12 + Number(meta(row, 'overdue_months'))) + ' months before this date'">
                            Overdue {{ meta(row, 'overdue_months') }} mo
                        </div>
                        <div v-if="leaseEnds(row)" class="cell-sub" title="Fixed term without rollover in Buildium: the new rent is not entered automatically">
                            Lease ends {{ leaseEnds(row) }}
                        </div>
                    </template>
                </el-table-column>
                <el-table-column label="PM (Buildium)" width="120">
                    <template slot-scope="{row}">{{ managerName(row) || '—' }}</template>
                </el-table-column>
                <el-table-column label="Assigned to" width="150">
                    <template slot-scope="{row}">{{ row.user ? row.user.nickname : '' }}</template>
                </el-table-column>
                <el-table-column label="Status" width="175">
                    <template slot-scope="{row}">
                        <span v-for="s in status(row)" :key="s.text" class="pill" :class="'pill-' + s.kind" :title="s.title || null">{{ s.text }}</span>
                    </template>
                </el-table-column>
                <el-table-column label="Created" width="105" align="right">
                    <template slot-scope="{row}">{{ (row.created_at || '').slice(0, 10) }}</template>
                </el-table-column>
            </el-table>
            <div class="list-footer">
                <span class="text-muted">{{ total }} form{{ total === 1 ? '' : 's' }}</span>
                <el-pagination background layout="prev, pager, next" :total="total" :page-size="pageSize"
                               :current-page="page" @current-change="onPageChange"/>
            </div>
        </section>

        <bulk-bar :count="selected.length" @clear="$refs.table.clearSelection()">
            <el-button v-if="isAdmin && selectedHeld.length" size="small" type="primary" @click="releaseSelected">
                Release {{ selectedHeld.length }} to property managers
            </el-button>
            <el-button v-if="canSend && isRtb7 && selected.some(f => f.status === 'draft')" size="small" type="primary" @click="confirmSend()">Confirm &amp; send to tenants</el-button>
            <el-button v-if="canConfirmSelection" size="small" @click="confirm()">Confirm only</el-button>
            <el-button v-if="isRtb7" size="small" @click="forward()">Forward to…</el-button>
            <el-button size="small" @click="download()">Download PDFs</el-button>
            <el-button size="small" @click="send()">Send by email</el-button>
            <el-dropdown trigger="click" @command="onBulkMore">
                <el-button size="small">More <i class="el-icon-arrow-down el-icon--right"></i></el-button>
                <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item v-if="isRtb7" command="skip">No increase this year…</el-dropdown-item>
                    <el-dropdown-item v-if="isRtb7 && isAdmin" command="withdraw">Increase withdrawn (not applied)…</el-dropdown-item>
                    <el-dropdown-item command="publish">Publish to Buildium</el-dropdown-item>
                    <el-dropdown-item command="export">Export to Excel</el-dropdown-item>
                    <el-dropdown-item command="delete" divided class="danger-item">Delete…</el-dropdown-item>
                </el-dropdown-menu>
            </el-dropdown>
        </bulk-bar>

        <form-detail-panel :form="detail" :can-send="canSend" @close="detail = null" @confirm="confirm([detail])" @confirm-send="confirmSend([detail])" @forward="forward([detail])"
                           @send="send([detail])" @download="download([detail])" @publish="publish([detail])"
                           @delete="remove([detail])"/>

        <modal-send-mail ref="mailer" :notes="sendNotes" @send="onSend"/>
        <el-dialog title="Forward to a colleague" :visible.sync="forwardDialog" width="420px" append-to-body>
            <p>The notices move to the colleague, who gets an email with a personal link to confirm and send them. Nothing is sent to tenants and Buildium is not changed now.</p>
            <el-select v-model="forwardTo" filterable placeholder="Choose a colleague" style="width: 100%">
                <el-option v-for="item in agents" :key="item.id"
                           :label="item.nickname + (item.email ? ' (' + item.email + ')' : '')" :value="item.id"/>
            </el-select>
            <span slot="footer">
                <el-button @click="forwardDialog = false">Cancel</el-button>
                <el-button type="primary" :disabled="!forwardTo" :loading="loading" @click="submitForward">Forward</el-button>
            </span>
        </el-dialog>
    </div>
</template>
